import React from "react";
import { Link } from "react-router-dom";

const PEN_IMAGE = "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/hero/service-bell.jpg";

const milestones = [
    { y: "1998", t: "Founded as Les Concierges®", b: "An idea: that good service is the most underrated form of luxury — and the most under-used answer to employee stress." },
    { y: "2008", t: "The registered concierge® mark", b: "We secured the legal mark — the only company in India that holds it. The ® matters; it is the legal equivalent of Champagne being from Champagne." },
    { y: "2014", t: "One million employees served", b: "Across three hundred-plus corporate clients, including Fortune 500 names. The hidden costs of stressed-out employees, quietly answered." },
    { y: "2018", t: "Backed by Rare Enterprise", b: "The late Rakesh Jhunjhunwala's investment company joined our cap table." },
    { y: "2022", t: "Fulfilment in 150 countries", b: "What began as a Bangalore operations floor now completes requests in 150 countries through trusted partners — wherever your people travel for work or family." },
    { y: "2026", t: "Mira goes live", b: "The AI listens. The humans deliver. A new operating system for corporate concierge®, built on twenty-eight years of human transcripts." },
];

export default function About() {
    return (
        <>
            {/* Heading band */}
            <section className="pt-40 md:pt-48 pb-16 md:pb-24" data-testid="about-hero">
                <div className="container-prose grid md:grid-cols-12 gap-12 items-end">
                    <div className="md:col-span-7">
                        <p className="eyebrow">About · Les Concierges · Since 1998</p>
                        <h1 className="font-serif text-5xl md:text-7xl leading-[1.05] mt-6 reveal">
                            Employees do not stop being <span className="italic text-[hsl(var(--brass))]">parents, children, caregivers</span> when they come to work.
                        </h1>
                    </div>
                    <p className="md:col-span-5 text-[16px] md:text-[18px] leading-relaxed text-[hsl(var(--ink))] reveal" style={{ animationDelay: "0.15s" }}>
                        Since 1998, Les Concierges® has helped organisations take care of one of the most overlooked realities of working life. Life does not pause when office begins. We exist for that continuity — for the errands, the documents, the doctors, the parents, the children, the travel, the everything else. Anything. Anytime. Anywhere.
                    </p>
                </div>
            </section>

            {/* The essay */}
            <section className="pb-16 md:pb-24" data-testid="about-essay">
                <div className="container-prose max-w-3xl font-serif text-[20px] md:text-[24px] leading-[1.6] text-[hsl(var(--navy))] space-y-8">
                    <p>
                        Life continues. Errands pile up. Forms need filing. Doctors need booking. Parents need support. Passports, repairs, school admissions, travel plans, emergency purchases, government work, gifting, celebrations, daily chores and unexpected crises all compete for the same time and energy an employee is expected to bring to work.
                    </p>
                    <p>
                        That is where we come in. Les Concierges® is India's leading workforce concierge® and work-life assistance provider — a trusted human support system for the many personal tasks that sit outside an employee's job description but affect their productivity, peace of mind and quality of life.
                    </p>
                    <p>
                        From a simple errand to a complex government-related requirement, from a last-minute reservation to a deeply personal family need, we help employees get things done with care, discretion and experience. No request is too small. No situation is too complicated — as long as it is legal, ethical and possible to pursue.
                    </p>
                    <p>
                        We are every everyday-use app rolled into one. With one critical difference: behind every request is a trained human concierge® who understands context, urgency, sensitivity and trust. We do not pass users from one platform to another. We listen. We understand. We coordinate. We follow through.
                    </p>
                </div>
            </section>

            {/* Founder note + image */}
            <section className="pb-16 md:pb-24" data-testid="about-founder">
                <div className="container-prose grid md:grid-cols-12 gap-12">
                    <figure className="md:col-span-5">
                        <img
                            src={PEN_IMAGE}
                            alt="A white-gloved hand poised over a service bell"
                            className="w-full h-[420px] md:h-[560px] object-cover"
                        />
                    </figure>
                    <div className="md:col-span-6 md:col-start-7">
                        <p className="eyebrow">A note from the founder</p>

                        <div className="mt-8 space-y-6 text-[15px] md:text-[16px] leading-[1.75] text-[hsl(var(--ink))]" data-testid="about-founder-note">
                            <p>
                                I began Les Concierges® in 1998, long before <span className="italic">concierge</span> became part of India's corporate vocabulary.
                            </p>
                            <p>
                                The idea was simple then, and it remains simple today: people should not have to lose their best hours to life's endless coordination. Errands, documents, repairs, school admissions, doctor appointments, travel changes, home needs, family emergencies — these are not small things when they interrupt work, peace, and family life.
                            </p>
                            <p>
                                For twenty-eight years, we have helped companies, residents, cardholders, and private clients carry that invisible load. Across India, and across 150+ countries, our work has always been human, discreet, and accountable.
                            </p>
                            <p className="font-serif italic text-[20px] md:text-[24px] leading-snug text-[hsl(var(--navy))] pt-2">
                                Mira is the next chapter.
                            </p>
                            <p>
                                She listens, understands, and gathers every request with care. But the promise is still delivered by people: our concierge® teams, our service offices, our relationships, our follow-through, and our refusal to leave anything half-done.
                            </p>
                            <p className="font-serif italic text-[18px] md:text-[20px] leading-snug text-[hsl(var(--navy))]">
                                This is not a vendor platform. It is a private office for life's moving parts.
                            </p>
                            <p className="font-serif italic text-[18px] md:text-[20px] text-[hsl(var(--brass))]" data-testid="about-founder-couplet">
                                The AI listens. The humans deliver.
                            </p>
                        </div>

                        <div className="mt-10 pt-6 border-t border-[hsl(var(--brass)/0.4)]">
                            <p className="font-serif italic text-[18px] text-[hsl(var(--navy))]">Dipali Sikand</p>
                            <p className="eyebrow mt-2">Founder · Les Concierges® Services Pvt Ltd</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="section" style={{ background: "hsl(var(--cream))" }} data-testid="about-timeline">
                <div className="container-prose max-w-4xl">
                    <p className="eyebrow">1998 → 2026 · An arc</p>
                    <div className="mt-12">
                        {milestones.map((m, idx) => (
                            <div
                                key={m.y}
                                className="grid md:grid-cols-12 gap-6 md:gap-12 py-10 border-b border-[hsl(var(--brass)/0.3)] last:border-b-0"
                                data-testid={`milestone-${m.y}`}
                            >
                                <p className="md:col-span-2 font-serif italic text-[hsl(var(--brass))] text-3xl">{m.y}</p>
                                <div className="md:col-span-10">
                                    <p className="font-serif text-2xl md:text-[28px] leading-snug">{m.t}</p>
                                    <p className="mt-3 text-[15px] leading-relaxed text-[hsl(var(--ink))]">{m.b}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Three brands */}
            <section className="section" data-testid="about-brands">
                <div className="container-prose">
                    <p className="eyebrow">Three brand entities, one roof</p>
                    <div className="mt-10 grid md:grid-cols-3 gap-10">
                        {[
                            { t: "Les Concierges®", k: "Corporate", b: "Employee concierge® programmes for Fortune 500 and India's leading enterprises. The original brand. The registered mark." },
                            { t: "Club Concierge®", k: "Lifestyle & HNI", b: "Bespoke lifestyle access for individuals, families, and wealth-management partners — anywhere in 150 countries." },
                            { t: "The Doggy Company", k: "Pet life OS", b: "Launching May 2026. The same standard of care, redirected to a different family member." },
                        ].map((b) => (
                            <div key={b.t} className="persona-card">
                                <p className="eyebrow">{b.k}</p>
                                <p className="font-serif text-3xl mt-3">{b.t}</p>
                                <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--ink))]">{b.b}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section" style={{ background: "hsl(var(--paper))" }} data-testid="about-closer">
                <div className="container-prose max-w-3xl text-center">
                    <p className="font-serif italic text-[22px] md:text-[30px] leading-relaxed text-[hsl(var(--navy))]">
                        We are a private company built around private service. We do not advertise. Our growth has come, for thirty years, from one client telling another.
                    </p>
                    <Link to="/connect" className="btn-ghost mt-12" data-testid="about-cta-connect">
                        Speak to our Enterprise Team
                    </Link>
                </div>
            </section>
        </>
    );
}
